import { useFilterOptions } from '@/hooks/useFilterOptions'
import { useDashboardData } from '@/hooks/useDashboardData'
import { usePrefectureDashboardData } from '@/hooks/usePrefectureDashboardData'
import useFilterStore from '@/store/useFilterStore'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { RefreshCw, Search } from 'lucide-react'
import { useState, useMemo } from 'react'

const GlobalFilters = () => {
  const { data: filterOptions, isLoading } = useFilterOptions()
  const { mutate: mutateDashboard } = useDashboardData()
  const { mutate: mutatePrefecture } = usePrefectureDashboardData()
  const {
    selectedCategory,
    startYear,
    endYear,
    selectedPrefectures,
    selectedTeams,
    selectedGeneration,
    setSelectedCategory,
    setStartYear,
    setEndYear,
    setSelectedPrefectures,
    setSelectedTeams,
    setSelectedGeneration,
    resetFilters
  } = useFilterStore()
  
  // チーム検索用の状態
  const [teamSearchQuery, setTeamSearchQuery] = useState('')

  // チーム検索フィルタリング
  const filteredTeams = useMemo(() => {
    if (!filterOptions?.teams) return []
    if (!teamSearchQuery.trim()) return filterOptions.teams
    
    return filterOptions.teams.filter(team => 
      team.name.toLowerCase().includes(teamSearchQuery.toLowerCase())
    )
  }, [filterOptions?.teams, teamSearchQuery])

  // デバッグ用ログ
  // console.log('Filter state:', { startYear, endYear, selectedPrefectures, selectedTeams })

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  // データが存在しない場合の処理
  if (!filterOptions) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">
          <p>データの読み込みに失敗しました</p>
        </div>
      </div>
    )
  }



  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <div className="flex items-center justify-between mb-3 sm:mb-4 lg:mb-6">
        <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">フィルター</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            resetFilters()
            // データを再取得
            mutateDashboard()
            mutatePrefecture()
          }}
          className="flex items-center gap-1 lg:gap-2 text-xs lg:text-sm"
        >
          <RefreshCw className="h-3 w-3 lg:h-4 lg:w-4" />
          <span className="hidden sm:inline">リセット</span>
        </Button>
      </div>

      <div className="space-y-3 sm:space-y-4 lg:space-y-6">

        {/* 大会カテゴリフィルター */}
        <div>
          <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-2">
            大会カテゴリ
          </label>
          <Select
            value={selectedCategory || ''}
            onValueChange={(value) => {
              console.log('Category changed to:', value)
              setSelectedCategory(value || null)
              // データを再取得
              mutateDashboard()
              mutatePrefecture()
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="大会カテゴリを選択" />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.categories && filterOptions.categories.length > 0 ? (
                filterOptions.categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-data" disabled>
                  データがありません
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* 大会年度フィルター */}
        <div>
          <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-2">
            開始年度
          </label>
          <Select
            value={startYear?.toString() || ''}
            onValueChange={(value) => {
              console.log('Start year changed to:', value)
              setStartYear(value ? parseInt(value) : null)
              // データを再取得
              mutateDashboard()
              mutatePrefecture()
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="開始年度を選択" />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.tournaments && filterOptions.tournaments.length > 0 ? (
                filterOptions.tournaments.map((tournament) => (
                  <SelectItem key={tournament.year} value={tournament.year.toString()}>
                    {tournament.year}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-data" disabled>
                  データがありません
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-2">
            終了年度
          </label>
          <Select
            value={endYear?.toString() || ''}
            onValueChange={(value) => {
              console.log('End year changed to:', value)
              setEndYear(value ? parseInt(value) : null)
              // データを再取得
              mutateDashboard()
              mutatePrefecture()
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="終了年度を選択" />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.tournaments && filterOptions.tournaments.length > 0 ? (
                filterOptions.tournaments.map((tournament) => (
                  <SelectItem key={tournament.year} value={tournament.year.toString()}>
                    {tournament.year}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-data" disabled>
                  データがありません
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* 都道府県フィルター */}
        <div>
          <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-2">
            都道府県
          </label>
          <Select
            value=""
            onValueChange={(value) => {
              const prefectureId = parseInt(value)
              console.log('Prefecture selected:', prefectureId)
              if (!selectedPrefectures.includes(prefectureId)) {
                setSelectedPrefectures([...selectedPrefectures, prefectureId])
                // データを再取得
                mutateDashboard()
                mutatePrefecture()
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="都道府県を選択" />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.prefectures && filterOptions.prefectures.length > 0 ? (
                filterOptions.prefectures.map((prefecture) => (
                  <SelectItem key={prefecture.id} value={prefecture.id.toString()}>
                    {prefecture.name}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-data" disabled>
                  データがありません
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          {selectedPrefectures.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {selectedPrefectures.map((id) => {
                const prefecture = filterOptions?.prefectures.find(p => p.id === id)
                return (
                  <span
                    key={id}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {prefecture?.name}
                    <button
                      onClick={() => {
                        setSelectedPrefectures(selectedPrefectures.filter(pId => pId !== id))
                        // データを再取得
                        mutateDashboard()
                        mutatePrefecture()
                      }}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                )
              })}
            </div>
          )}
        </div>

        {/* 世代フィルター */}
        <div>
          <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-2">
            世代
          </label>
          <Select
            value={selectedGeneration || ''}
            onValueChange={(value) => {
              console.log('Generation changed to:', value)
              setSelectedGeneration(value || null)
              // データを再取得
              mutateDashboard()
              mutatePrefecture()
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="世代を選択" />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.generations && filterOptions.generations.length > 0 ? (
                filterOptions.generations.map((generation) => (
                  <SelectItem key={generation.id} value={generation.id}>
                    {generation.name}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-data" disabled>
                  データがありません
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* チームフィルター */}
        <div>
          <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-2">
            チーム
          </label>
          
          {/* チーム検索入力 */}
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="チーム名で検索..."
              value={teamSearchQuery}
              onChange={(e) => setTeamSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select
            value=""
            onValueChange={(value) => {
              const teamId = parseInt(value)
              console.log('Team selected:', teamId)
              if (!selectedTeams.includes(teamId)) {
                setSelectedTeams([...selectedTeams, teamId])
                // データを再取得
                mutateDashboard()
                mutatePrefecture()
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="チームを選択" />
            </SelectTrigger>
            <SelectContent>
              {filteredTeams.length > 0 ? (
                filteredTeams.map((team) => (
                  <SelectItem key={team.id} value={team.id.toString()}>
                    {team.name}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-data" disabled>
                  {teamSearchQuery.trim() ? '検索結果がありません' : 'データがありません'}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          {selectedTeams.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {selectedTeams.map((id) => {
                const team = filterOptions?.teams.find(t => t.id === id)
                return (
                  <span
                    key={id}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    {team?.name}
                    <button
                      onClick={() => {
                        setSelectedTeams(selectedTeams.filter(tId => tId !== id))
                        // データを再取得
                        mutateDashboard()
                        mutatePrefecture()
                      }}
                      className="ml-1 text-green-600 hover:text-green-800"
                    >
                      ×
                    </button>
                  </span>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GlobalFilters
